import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthenticationClient } from '../../core/service/authentication-client.service';
import { deriveSessionValidToEpochMs } from '../../core/helper/session-storage';
import { NavigationService } from '../../core/service/navigation.service';
import { SessionService } from '../../core/service/session.service';
import { SidebarService } from '../../core/service/sidebar.service';
import { AccountDefinition } from '../model/account-definition';
import { AutoLogoutService } from './auto-logout-service';
import { AccountService } from './data/account.service';
import { ProfileService } from './data/profile.service';
import { Features, ModuleNames } from '../model/account-definition';
import { LicenseService } from './data/license.service';
import { BankIdService } from './data/bankId.service';
import { environment } from '../../../environments/environment';
import { BasicOwnerConfig } from '../model/basic-owner-config';
import { ImportantNoticePresentationService } from './important-notice-presentation.service';
import { AmplitudeService } from '../../core/service/tracking/amplitude.service';
import { FullStoryService } from '../../core/service/tracking/fullstory.service';
import { UserpilotService } from '../../core/service/tracking/userpilot.service';

function setHotjarAttributes(acc: AccountDefinition | null): void {
  try {
    if (acc?.id?.length) {
      hj('identify', acc.id, {
        production: environment.production ?? false,
        modules: Array.isArray(acc.modules) ? acc.modules.join(',') : '',
      });
      console.log('Hotjar attributes updated');
    } else {
      hj('identify', null, {});
      console.log('Hotjar attributes cleared');
    }
  } catch {
    console.warn('Failed to update Hotjar attributes');
  }
}

@Injectable({ providedIn: 'root' })
export class UserAccountService {
  profileSettings: BasicOwnerConfig;
  constructor(
    private readonly _client: AuthenticationClient,
    private readonly _dataService: AccountService,
    private readonly _licenseService: LicenseService,
    private readonly _navigation: NavigationService,
    private readonly _session: SessionService,
    private readonly _autoLogoutService: AutoLogoutService,
    private readonly _profileService: ProfileService,
    private readonly _sidebarService: SidebarService,
    private readonly _bankIdService: BankIdService,
    private readonly _importantNoticePresentationService: ImportantNoticePresentationService,
    private readonly _errorHandler: ErrorHandler,
    private readonly _ngZone: NgZone
  ) { }

  private _account: Observable<AccountDefinition> | null = null;
  private _currentAccount: AccountDefinition | null = null;
  private _accountLogout = new Subject<void>();
  private readonly _authenticatedSessionCleared = new Subject<void>();
  private _refreshProfileSettings = new BehaviorSubject<null>(null);
  private _refreshAccountDefinition = new BehaviorSubject<null>(null);
  private readonly _onIdleLogout = (): void => {
    void this.logout();
  };

  get current(): Observable<AccountDefinition> {
    return this.getOrCreateAccount().pipe(
      tap(account => this.configureIdleLogout(account)),
      catchError(err => {
        void this.logout();
        return throwError(() => err);
      })
    );
  }

  get authenticatedSessionCleared$(): Observable<void> {
    return this._authenticatedSessionCleared.asObservable();
  }

  private getOrCreateAccount(): Observable<AccountDefinition> {
    if (!this._account) {
      this._account = combineLatest([
        this._refreshProfileSettings.pipe(switchMap(() => this._profileService.getConfig())),
        this._refreshAccountDefinition.pipe(switchMap(() => this._dataService.getAccountDefinition())),
      ]).pipe(
        takeUntil(this._accountLogout),
        map(([ps, ad]) => {
          this.profileSettings = ps;
          setHotjarAttributes(ad);
          const account = new AccountDefinition(ad, this.profileSettings);
          this._currentAccount = account;
          const tracking = environment.trialTracking;
          if (tracking) {
            this._ngZone.runOutsideAngular(() => {
              if (tracking.fullStoryEnabled) {
                FullStoryService.identifyUser(account);
              }
              if (tracking.userpilotEnabled) {
                UserpilotService.identifyUser(account);
              }
              if (tracking.amplitudeEnabled) {
                AmplitudeService.identifyUser(account);
              }
            });
          }
          return account;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this._account;
  }

  get currentAccount(): AccountDefinition | null {
    return this._currentAccount;
  }

  private _isAdmin$?: Observable<boolean>;
  get isAdmin$(): Observable<boolean> {
    if (!this._isAdmin$) {
      this._isAdmin$ = this.current.pipe(
        map(account => !!account?.hasAccess(ModuleNames.System, Features.Admin)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this._isAdmin$;
  }

  refreshProfileSettings(): void {
    this._refreshProfileSettings.next(null);
  }

  refreshAccountDefinition(): void {
    this._refreshAccountDefinition.next(null);
  }

  bankIdAuthStart(isMobile: boolean, thisUnit: boolean) {
    return this._bankIdService.bankIdAuthStart(isMobile, thisUnit);
  }

  bankIdAuthStatus(isMobile: boolean, thisUnit: boolean, orderReference: string) {
    return this._bankIdService.bankIdAuthStatus(isMobile, thisUnit, orderReference);
  }

  bankIdCancel(orderReference: string) {
    return this._bankIdService.bankIdCancel(orderReference);
  }

  login(userName: string, password: string, saveSession: boolean): Observable<any> {
    return this._client.authenticate(userName, password).pipe(
      switchMap(res => this.establishSession(res.access_token, res.expires_in, saveSession)),
      tap(account => this.navigateAfterLogin(account))
    );
  }

  loginBankId(orderReference: string): Observable<any> {
    return this._client.authenticateBankId(orderReference).pipe(
      switchMap(res => this.establishSession(res.access_token, res.expires_in, false)),
      tap(account => this.navigateAfterLogin(account))
    );
  }

  private navigateAfterLogin(account: AccountDefinition): void {
    const redirectUrl = this._session.redirectUrl;
    this._session.redirectUrl = null;

    if (redirectUrl) {
      // The stored URL carries its own query string; navigate([url]) would encode the "?" into the path.
      this._navigation.navigateByUrl(redirectUrl);
    } else {
      this._navigation.navigate([account.homeUrl], { queryParamsHandling: 'preserve' });
    }
  }

  establishSession(
    accessToken: string,
    expiresInSeconds: number,
    persistent: boolean
  ): Observable<AccountDefinition> {
    this.clearAuthenticatedSession();
    this._importantNoticePresentationService.clearAll();
    if (!accessToken) {
      return throwError(() => new Error('Cannot establish authenticated session: access token is missing.'));
    }
    if (deriveSessionValidToEpochMs(expiresInSeconds) === undefined) {
      return throwError(() => new Error('Cannot establish authenticated session: expires_in is invalid.'));
    }
    if (!this._session.start(accessToken, expiresInSeconds, persistent)) {
      return throwError(() => new Error(
        'Cannot establish authenticated session: session persistence or verification failed.'
      ));
    }

    return this.getOrCreateAccount().pipe(
      take(1),
      tap(account => {
        if (!this._session.setHomeUrl(account.homeUrl)) {
          throw new Error(
            'Cannot complete authenticated session: home URL persistence or verification failed.'
          );
        }
        this.configureIdleLogout(account);
      }),
      catchError(err => {
        this.clearAuthenticatedSession();
        return throwError(() => err);
      })
    );
  }

  private configureIdleLogout(account: AccountDefinition): void {
    this._autoLogoutService.start(account.timeoutInMinutes, this._onIdleLogout);
  }

  clearAuthenticatedSession(): void {
    this._authenticatedSessionCleared.next();
    this._importantNoticePresentationService.clearAll();
    this._session.stop();
    this._accountLogout.next();
    this._account = null;
    this._currentAccount = null;
    this._isAdmin$ = undefined;
    this._licenseService?.clearLimitsCache?.();
    this._autoLogoutService.stop();
    this._sidebarService.toggleSidebar(false);
    setHotjarAttributes(null);
    const tracking = environment.trialTracking;
    if (tracking) {
      this._ngZone.runOutsideAngular(() => {
        if (tracking.fullStoryEnabled) {
          FullStoryService.resetUser();
        }
        if (tracking.userpilotEnabled) {
          UserpilotService.resetUser();
        }
        if (tracking.amplitudeEnabled) {
          AmplitudeService.resetUser();
        }
      });
    }
  }

  /** Resolves false when navigation to the login screen is cancelled or fails. Session cleanup has already completed. */
  async logout(): Promise<boolean> {
    this.clearAuthenticatedSession();
    try {
      return await this._navigation.navigateLogin();
    } catch {
      // Static message: navigation errors may contain a URL with sensitive query parameters.
      this._errorHandler.handleError(new Error(
        '[YourLandlord] Failed to navigate to the login screen after logout.'
      ));
      return false;
    }
  }
}
